import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { doc, getDoc, getDocs, limit, query, where, collection } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

import GameWithClosingNow from "../components/games/GameWithClosingNow";
import { useAuth } from "../context/AuthContext";
import { db, functions } from "../firebase";
import { gameGuessErrorMessage } from "../utils/gameGuessError";
import "./WordDrop.css";

const KEY_ROWS = ["QWERTYUIOP", "ASDFGHJKL", "ZXCVBNM"];
const RANK = { absent: 1, present: 2, correct: 3 };
const SHARE_GLYPH = { correct: "🟧", present: "🟨", absent: "⬛" };

function emptyBoard() {
  return { rows: [], current: "", status: "playing", error: "" };
}

function rowTiles(row) {
  if (Array.isArray(row)) return row;
  if (row && Array.isArray(row.tiles)) return row.tiles;
  if (typeof row === "string") return row.split("|").filter(Boolean);
  return [];
}

function boardFromScore(score) {
  if (!score) return emptyBoard();
  const guesses = Array.isArray(score.guesses) ? score.guesses : [];
  const tileRows = Array.isArray(score.tileRows) ? score.tileRows : [];
  const count = Number(score.guessCount || guesses.length || 0);
  const solved = score.solved === true;
  const ended = solved || count >= 6;
  return {
    rows: guesses.map((guess, index) => ({
      guess: String(guess || ""),
      tiles: rowTiles(tileRows[index]),
    })),
    current: "",
    status: solved ? "solved" : ended ? "failed" : "playing",
    error: "",
  };
}

function todayKey() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function puzzleNumber(dateStr) {
  const [year, month, day] = dateStr.split("-").map(Number);
  const start = Date.UTC(year, 0, 0);
  const current = Date.UTC(year, month - 1, day);
  return Math.round((current - start) / 86400000);
}

function firstName(displayName) {
  const name = String(displayName || "").trim();
  if (!name) return "Creator";
  return name.split(/\s+/)[0];
}

function initials(displayName) {
  const parts = String(displayName || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "•";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function letterLabel(grid) {
  return grid === 6 ? "6-letter" : "5-letter";
}

function pointsForResult(solved, guessCount) {
  if (!solved) return 0;
  return Math.max(0, 7 - Number(guessCount || 0));
}

function colorRows(score, grid) {
  if (Array.isArray(score?.tileRows) && score.tileRows.length) {
    return score.tileRows.map((row) => {
      if (Array.isArray(row)) return row;
      if (Array.isArray(row?.tiles)) return row.tiles;
      if (typeof row === "string") return row.split("|").filter(Boolean);
      return [];
    }).filter((row) => row.length);
  }
  const count = Number(score?.guessCount || 0);
  if (!count) return [];
  return Array.from({ length: Math.min(count, 6) }, () => Array.from({ length: grid }, () => "unknown"));
}

function cardCopy(displayName, score, grid) {
  const name = firstName(displayName);
  const letters = letterLabel(grid);
  const played = score && Number(score.grid) === grid && Number(score.guessCount) > 0;
  if (!played) {
    return {
      title: `${name} hasn't played the ${letters}`,
      subtitle: "Be first on this board",
      rows: [],
    };
  }
  if (score.solved === true) {
    const beatIn = Number(score.guessCount) - 1;
    return {
      title: `${name} got the ${letters} in ${score.guessCount}`,
      subtitle: beatIn >= 1
        ? `Solve it in ${beatIn} to take the lead on ${name}'s board`
        : `${name} already finished in 1`,
      rows: colorRows(score, grid),
    };
  }
  return {
    title: `${name} didn't solve the ${letters}`,
    subtitle: "A finish puts you ahead on this board",
    rows: colorRows(score, grid),
  };
}

function comparisonLine(displayName, score, grid, solved, guessCount) {
  const name = firstName(displayName);
  const letters = letterLabel(grid);
  const played = score && Number(score.grid) === grid && Number(score.guessCount) > 0;
  if (!played) return `${name} hasn't played the ${letters} yet.`;
  if (score.solved === true && solved) {
    if (Number(guessCount) === Number(score.guessCount)) {
      return `You tied ${name} — both in ${guessCount}.`;
    }
    return `${name} got it in ${score.guessCount}. You got it in ${guessCount}.`;
  }
  if (score.solved === true) return `${name} got the ${letters} in ${score.guessCount}.`;
  if (solved) return `You got it. ${name} didn't solve the ${letters}.`;
  return `${name} didn't solve the ${letters} either.`;
}

function bestKeyState(rows) {
  const best = {};
  rows.forEach((row) => {
    const letters = String(row.guess || "").split("");
    letters.forEach((letter, index) => {
      const tile = row.tiles?.[index];
      if (!RANK[tile]) return;
      if (!best[letter] || RANK[tile] > RANK[best[letter]]) best[letter] = tile;
    });
  });
  return best;
}

function shareText(puzzleNo, grid, rows) {
  const body = rows
    .map((row) => (row.tiles || []).map((tile) => SHARE_GLYPH[tile] || "⬜").join(""))
    .join("\n");
  return `Word Drop · No. ${puzzleNo}\n${grid} letters\n${body}`;
}

function shareImage(rows) {
  const tile = 28;
  const gap = 6;
  const width = rows.reduce((max, row) => Math.max(max, (row.tiles || []).length), 0);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, width) * (tile + gap) + gap;
  canvas.height = Math.max(1, rows.length) * (tile + gap) + gap;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#FAFAFA";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const fill = { correct: "#C4790A", present: "#F2D27A", absent: "#6B7280" };
  rows.forEach((row, rowIndex) => {
    (row.tiles || []).forEach((tileState, index) => {
      ctx.fillStyle = fill[tileState] || "#E8ECF2";
      const x = gap + index * (tile + gap);
      const y = gap + rowIndex * (tile + gap);
      ctx.beginPath();
      ctx.roundRect(x, y, tile, tile, 6);
      ctx.fill();
    });
  });
  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
}

export default function WordDrop({ gameSwitch = null }) {
  const { currentUser, userProfile } = useAuth();
  const [searchParams] = useSearchParams();
  const date = useMemo(() => todayKey(), []);
  const puzzleNo = puzzleNumber(date);
  const [grid, setGrid] = useState(5);
  const [boards, setBoards] = useState({ 5: emptyBoard(), 6: emptyBoard() });
  const [shake, setShake] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sheet, setSheet] = useState(null);
  const [shareNote, setShareNote] = useState("");
  const [creator, setCreator] = useState(null);
  const [creatorScore, setCreatorScore] = useState(null);
  const [creatorNote, setCreatorNote] = useState("");

  const gridRef = useRef(grid);
  const boardsRef = useRef(boards);
  const busyRef = useRef(false);
  const sheetRef = useRef(sheet);
  const creatorIdRef = useRef("");
  gridRef.current = grid;
  boardsRef.current = boards;
  sheetRef.current = sheet;

  const board = boards[grid];

  const reloadCreatorScore = useCallback(async (creatorId, size = grid) => {
    if (!creatorId) {
      setCreatorScore(null);
      return;
    }
    try {
      const snap = await getDoc(doc(db, "wordDrop_scores", `${date}_${creatorId}_${creatorId}_${size}`));
      setCreatorScore(snap.exists() ? snap.data() : null);
      setCreatorNote("");
    } catch (err) {
      setCreatorScore(null);
      setCreatorNote(err?.code === "permission-denied"
        ? "This board is locked to the creator's own score."
        : "This board couldn't be loaded.");
    }
  }, [date, grid]);

  useEffect(() => {
    let alive = true;
    const param = (searchParams.get("creator") || "").trim();

    async function resolveCreatorId() {
      if (param) {
        const direct = await getDoc(doc(db, "users", param));
        if (direct.exists()) return direct.id;
        const handle = await getDoc(doc(db, "usernames", param.toLowerCase()));
        const uid = handle.exists() ? handle.data()?.uid : "";
        if (uid) return uid;
      }
      if (userProfile?.isCreator && currentUser?.uid) return currentUser.uid;
      const followed = Array.isArray(userProfile?.following) ? userProfile.following.filter(Boolean) : [];
      if (followed.length) return followed[0];
      if (!currentUser?.uid) return "";
      const follows = await getDocs(query(
        collection(db, "follows"),
        where("followerId", "==", currentUser.uid),
        limit(1)
      ));
      return follows.docs[0]?.data()?.creatorId || "";
    }

    (async () => {
      try {
        const creatorId = await resolveCreatorId();
        if (!alive) return;
        creatorIdRef.current = creatorId || "";
        if (!creatorId) {
          setCreator(null);
          setCreatorScore(null);
          setCreatorNote("Open a creator's board to play.");
          return;
        }
        const profile = await getDoc(doc(db, "users", creatorId));
        if (!alive) return;
        setCreator({
          id: creatorId,
          displayName: profile.exists() ? (profile.data()?.displayName || "Creator") : "Creator",
        });
        await reloadCreatorScore(creatorId);
        if (currentUser?.uid) {
          const snaps = await Promise.all([5, 6].map((size) => (
            getDoc(doc(db, "wordDrop_scores", `${date}_${creatorId}_${currentUser.uid}_${size}`))
          )));
          if (!alive) return;
          setBoards((prev) => {
            const next = { ...prev };
            snaps.forEach((snap, index) => {
              const size = index === 0 ? 5 : 6;
              if (prev[size].rows.length > 0 || prev[size].current) return;
              if (!snap.exists()) return;
              next[size] = boardFromScore(snap.data());
            });
            return next;
          });
        }
      } catch (err) {
        if (!alive) return;
        setCreatorNote(err?.message || "This board couldn't be loaded.");
      }
    })();

    return () => {
      alive = false;
    };
  }, [currentUser, userProfile, searchParams, reloadCreatorScore]);

  const setBoardError = useCallback((size, error) => {
    setBoards((prev) => ({ ...prev, [size]: { ...prev[size], error } }));
  }, []);

  const submitCurrent = useCallback(async () => {
    const size = gridRef.current;
    const current = boardsRef.current[size];
    if (!current || current.status !== "playing" || busyRef.current || sheetRef.current) return;
    if (current.current.length !== size) {
      setShake(false);
      requestAnimationFrame(() => setShake(true));
      return;
    }
    if (!currentUser?.uid) {
      setBoardError(size, "Sign in to play.");
      return;
    }
    if (!creatorIdRef.current) {
      setBoardError(size, "Open a creator's board to play.");
      return;
    }

    busyRef.current = true;
    setBusy(true);
    try {
      const submit = httpsCallable(functions, "submitWordDropGuess");
      const res = await submit({
        date,
        creatorId: creatorIdRef.current,
        grid: size,
        guess: current.current,
      });
      const data = res.data || {};
      const tiles = Array.isArray(data.tiles) ? data.tiles : [];
      if (tiles.length !== size) {
        setBoardError(size, "That guess didn't go through. Try again.");
        return;
      }
      const solved = data.solved === true;
      const ended = data.ended === true || solved;
      const guessCount = Number(data.guessCount || 0);
      setBoards((prev) => {
        const latest = prev[size];
        return {
          ...prev,
          [size]: {
            ...latest,
            rows: [...latest.rows, { guess: latest.current, tiles }],
            current: "",
            status: solved ? "solved" : ended ? "failed" : "playing",
            error: "",
          },
        };
      });
      if (ended) {
        setSheet({ grid: size, solved, guessCount });
        reloadCreatorScore(creatorIdRef.current);
      }
    } catch (err) {
      setBoardError(size, gameGuessErrorMessage(err, "word"));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, [currentUser, date, reloadCreatorScore, setBoardError]);

  const typeLetter = useCallback((letter) => {
    const size = gridRef.current;
    if (busyRef.current || sheetRef.current) return;
    setBoards((prev) => {
      const current = prev[size];
      if (!current || current.status !== "playing" || current.current.length >= size) return prev;
      return {
        ...prev,
        [size]: { ...current, current: `${current.current}${letter}`, error: "" },
      };
    });
  }, []);

  const deleteLetter = useCallback(() => {
    const size = gridRef.current;
    if (busyRef.current || sheetRef.current) return;
    setBoards((prev) => {
      const current = prev[size];
      if (!current || current.status !== "playing" || !current.current) return prev;
      return {
        ...prev,
        [size]: { ...current, current: current.current.slice(0, -1), error: "" },
      };
    });
  }, []);

  useEffect(() => {
    const onKey = (event) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const tag = event.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (event.key === "Enter") {
        event.preventDefault();
        submitCurrent();
        return;
      }
      if (event.key === "Backspace") {
        event.preventDefault();
        deleteLetter();
        return;
      }
      if (/^[a-zA-Z]$/.test(event.key)) {
        event.preventDefault();
        typeLetter(event.key.toUpperCase());
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [deleteLetter, submitCurrent, typeLetter]);

  const keyState = bestKeyState(board.rows);
  const other = grid === 5 ? 6 : 5;
  const otherBoard = boards[other];
  const bothDone = boards[5].status !== "playing" && boards[6].status !== "playing";
  const copy = cardCopy(creator?.displayName, creatorScore, grid);
  const sheetBoard = sheet ? boards[sheet.grid] : null;
  const sheetPoints = sheet ? pointsForResult(sheet.solved, sheet.guessCount) : 0;
  const leaderboard = `/games/leaderboard?creator=${encodeURIComponent(creator?.id || "")}&grid=${grid}&date=${date}`;

  const filledRows = [
    ...board.rows.map((row) => ({ letters: String(row.guess || "").split(""), tiles: row.tiles || [], locked: true })),
  ];
  if (board.status === "playing" && filledRows.length < 6) {
    filledRows.push({
      letters: board.current.split(""),
      tiles: [],
      locked: false,
    });
  }
  while (filledRows.length < 6) filledRows.push({ letters: [], tiles: [], locked: false });

  async function onShare() {
    if (!sheetBoard) return;
    const text = shareText(puzzleNo, sheet.grid, sheetBoard.rows);
    setShareNote("");
    const blob = await shareImage(sheetBoard.rows);
    let shared = false;
    try {
      const file = blob ? new File([blob], "word-drop.png", { type: "image/png" }) : null;
      if (file && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ text, files: [file] });
        shared = true;
      } else if (navigator.share) {
        await navigator.share({ text });
        shared = true;
      }
    } catch (err) {
      if (err?.name === "AbortError") return;
    }
    let copied = false;
    try {
      await navigator.clipboard.writeText(text);
      copied = true;
    } catch (_) {}
    if (blob) {
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "word-drop.png";
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
    if (shared) setShareNote("Shared.");
    else if (copied) setShareNote("Copied the color grid and downloaded the image.");
    else if (blob) setShareNote("Downloaded the color grid.");
    else setShareNote("Share didn't go through.");
  }

  return (
    <GameWithClosingNow>
    <div className="wd-page">
      {gameSwitch}
      <header className="wd-header">
        <div className="wd-mark">100VO</div>
        <div className="wd-puzzle">WORD DROP · No. {puzzleNo}</div>
        <Link className="wd-boards" to={leaderboard}>Boards</Link>
      </header>

      <div className="wd-segs" role="tablist" aria-label="Word length">
        {[5, 6].map((size) => {
          const status = boards[size].status;
          return (
            <button
              key={size}
              type="button"
              role="tab"
              aria-selected={grid === size}
              className={`wd-seg${grid === size ? " active" : ""}`}
              onClick={() => {
                setGrid(size);
                setSheet(null);
              }}
            >
              <span className={`wd-dot${status === "playing" ? "" : ` wd-dot--${status}`}`} />
              {size} letters
            </button>
          );
        })}
      </div>

      <Link className="wd-creator" to={leaderboard}>
        <div className="wd-avatar" aria-hidden="true">{initials(creator?.displayName)}</div>
        <div className="wd-creator-copy">
          <div className="wd-creator-title">{creator ? copy.title : "Word Drop"}</div>
          <div className="wd-creator-sub">{creatorNote || copy.subtitle}</div>
        </div>
        {copy.rows.length > 0 && (
          <div className="wd-mini" aria-hidden="true">
            {copy.rows.map((row, rowIndex) => (
              <div className="wd-mini-row" key={`mini-${rowIndex}`}>
                {row.map((tile, index) => (
                  <span className={`wd-mini-tile ${tile === "unknown" ? "" : tile}`} key={`${rowIndex}-${index}`} />
                ))}
              </div>
            ))}
          </div>
        )}
      </Link>

      <div className="wd-grid" aria-label={`${grid} letter guesses`}>
        {filledRows.map((row, rowIndex) => {
          const isCurrent = board.status === "playing" && rowIndex === board.rows.length;
          return (
            <div
              className={`wd-row${isCurrent && shake ? " is-shaking" : ""}`}
              key={`row-${grid}-${rowIndex}`}
              onAnimationEnd={() => setShake(false)}
            >
              {Array.from({ length: grid }, (_, index) => {
                const letter = row.letters[index] || "";
                const tile = row.locked ? (row.tiles[index] || "") : "";
                const typed = !row.locked && letter;
                return (
                  <div
                    key={`${grid}-${rowIndex}-${index}`}
                    className={`wd-tile${tile ? ` ${tile}` : ""}${typed ? " typed" : ""}`}
                  >
                    {letter}
                  </div>
                );
              })}
            </div>
          );
        })}
        <div className="wd-caption">
          {grid === 6
            ? "Six letters, six guesses. Same word for everyone today."
            : "Five letters, six guesses. Same word for everyone today."}
        </div>
      </div>
      {board.error && <div className="wd-error" role="alert">{board.error}</div>}

      <div className="wd-keys">
        {KEY_ROWS.map((letters, rowIndex) => (
          <div className="wd-key-row" key={letters}>
            {rowIndex === 2 && (
              <button type="button" className="wd-key wide" onClick={submitCurrent} disabled={busy}>ENTER</button>
            )}
            {letters.split("").map((letter) => (
              <button
                type="button"
                key={letter}
                className={`wd-key${keyState[letter] ? ` ${keyState[letter]}` : ""}`}
                onClick={() => typeLetter(letter)}
                disabled={busy || board.status !== "playing"}
              >
                {letter}
              </button>
            ))}
            {rowIndex === 2 && (
              <button type="button" className="wd-key wide" onClick={deleteLetter} disabled={busy}>DEL</button>
            )}
          </div>
        ))}
      </div>

      {sheet && sheetBoard && (
        <div className="wd-sheet-backdrop" onClick={() => setSheet(null)}>
          <div
            className="wd-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="wd-sheet-title"
            onClick={(event) => event.stopPropagation()}
          >
            <p className="wd-sheet-eyebrow">{sheet.solved ? "Solved" : "No more guesses"}</p>
            <h2 id="wd-sheet-title" className="wd-sheet-headline">
              {sheet.solved ? "You got it" : "That's the round"}
            </h2>
            <p className="wd-sheet-points">
              {sheetPoints} {sheetPoints === 1 ? "point" : "points"}
            </p>
            <p className="wd-sheet-compare">
              {comparisonLine(creator?.displayName, creatorScore, sheet.grid, sheet.solved, sheet.guessCount)}
            </p>
            <div className="wd-sheet-grid" aria-hidden="true">
              {sheetBoard.rows.map((row, rowIndex) => (
                <div className="wd-mini-row" key={`sheet-${rowIndex}`}>
                  {(row.tiles || []).map((tile, index) => (
                    <span className={`wd-mini-tile ${tile}`} key={`${rowIndex}-${index}`} />
                  ))}
                </div>
              ))}
            </div>
            <div className="wd-sheet-actions">
              {otherBoard.status === "playing" && (
                <button
                  type="button"
                  className="wd-sheet-primary"
                  onClick={() => {
                    setGrid(other);
                    setSheet(null);
                  }}
                >
                  Play the {other}-letter
                </button>
              )}
              {bothDone && (
                <>
                  <Link className="wd-sheet-primary" to={leaderboard}>See the board</Link>
                  <button type="button" className="wd-sheet-secondary" onClick={onShare}>Share</button>
                </>
              )}
            </div>
            {shareNote && <p className="wd-share-note">{shareNote}</p>}
          </div>
        </div>
      )}
    </div>
    </GameWithClosingNow>
  );
}
